"use client";

import { useTranslations } from "@/lib/compat/i18n";
import clsx from "clsx";
import { Monogram } from "@/components/persona/Monogram";

const AGENT_ACCENT: Record<string, string> = {
  echo:     "var(--state-verified)",
  vega:     "var(--agent-vega)",
  atlas:    "var(--state-traced)",
  mercator: "var(--state-financed)",
  sage:     "var(--agent-sage)",
  eva:      "var(--state-verified)",
};

const AGENT_GLYPH: Record<string, string> = {
  echo:     "◇",
  vega:     "✦",
  atlas:    "◈",
  mercator: "◉",
  sage:     "◍",
  eva:      "E",
};

/**
 * AgentBubble is the only chrome the AI agents (and the Eva verification
 * officer) appear in. Per design principle 6: agents speak in 1 sentence
 * max per appearance. The bubble is anchored to a UI region — never a chat
 * sidebar.
 */
export function AgentBubble({
  agent,
  line,
  className,
}: {
  agent: keyof typeof AGENT_ACCENT;
  line: string;
  className?: string;
}) {
  const t = useTranslations("agents");
  const name = t(agent, { fallback: agent });
  return (
    <div className={clsx("inline-flex items-start gap-3 max-w-md", className)}>
      <Monogram name={name} glyph={AGENT_GLYPH[agent]} accent={AGENT_ACCENT[agent]} size={32} />
      <div className="rounded-md border border-state-verified/55 bg-state-verified/15 px-3 py-2 shadow-glass backdrop-blur-md">
        <p className="font-display text-[11px] font-bold uppercase tracking-wider text-state-verified">{name}</p>
        <p className="mt-0.5 text-[13px] leading-snug text-orbitra-paper">{line}</p>
      </div>
    </div>
  );
}
